import { useRef, useState } from 'react';
import { Paperclip, Upload, X, Loader2, ExternalLink, Plus } from 'lucide-react';
import { supabase, STORAGE_BUCKET } from '../../lib/supabase';

const SIGNED_URL_TTL = 60 * 60 * 24 * 365;

// Trunca por chars com ellipsis no MEIO — preserva inicio + extensao.
const truncateMiddle = (s, max = 40) => {
  const str = String(s || '');
  if (str.length <= max) return str;
  const keep = Math.floor((max - 1) / 2);
  return `${str.slice(0, keep)}…${str.slice(-keep)}`;
};

// Aceita array de { url, filename } e permite adicionar/remover multiplos
// PDFs em um so campo. Reusa a compressao do pdf-compress.js quando
// compress=true (dynamic import, so baixa na primeira anexacao).
export function MultiFileInput({
  value, onChange, onError, onToast,
  compress = false,
  pathPrefix = 'solicitacoes',
}) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [compressStatus, setCompressStatus] = useState('');

  const items = Array.isArray(value) ? value : [];

  const handlePick = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const rawFiles = Array.from(fileList);
    let compressor = null;
    if (compress) {
      try {
        compressor = await import('../../lib/pdf-compress');
      } catch (err) {
        console.warn('[MultiFileInput] falha ao carregar compressor:', err);
      }
    }

    const newItems = [...items];

    for (const raw of rawFiles) {
      // Valida PDF
      const isPdfMime = raw.type === 'application/pdf';
      const isPdfExt  = /\.pdf$/i.test(raw.name || '');
      if (!isPdfMime && !isPdfExt) {
        onError?.(`"${raw.name}" não é PDF. Só PDFs são aceitos.`);
        continue;
      }

      let file = raw;
      const originalName = raw.name || 'arquivo.pdf';

      // Compressao opcional (BHAZ)
      if (compressor) {
        try {
          setCompressStatus(`Comprimindo ${truncateMiddle(originalName, 30)}…`);
          const result = await compressor.compressPdf(file, {
            onProgress: (p) => {
              if (p.stage === 'rendering') {
                setCompressStatus(`Comprimindo ${truncateMiddle(originalName, 22)} · pág. ${p.page}/${p.total}`);
              }
            },
          });
          if (!result.skipped) {
            file = result.file;
            const pct = Math.round((1 - result.finalSize / result.originalSize) * 100);
            onToast?.(
              `${originalName}: ${compressor.fmtBytes(result.originalSize)} → ${compressor.fmtBytes(result.finalSize)} (-${pct}%)`,
              'ok'
            );
          }
        } catch (err) {
          console.warn('[MultiFileInput] compressao falhou, seguindo com original:', err);
        }
      }

      setCompressStatus('');
      setUploading(true);
      const safeName = originalName.replace(/[^\w.\-]+/g, '_');
      const path = `${pathPrefix}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(path, file, { contentType: 'application/pdf' });
      if (upErr) {
        setUploading(false);
        onError?.(`Falha no upload de "${originalName}": ${upErr.message}`);
        continue;
      }
      const { data: signed, error: signErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(path, SIGNED_URL_TTL);
      setUploading(false);
      if (signErr || !signed?.signedUrl) {
        onError?.(`Upload OK mas falhou gerar URL de "${originalName}"`);
        continue;
      }

      newItems.push({
        url: signed.signedUrl,
        filename: originalName,
      });
    }

    // Reset o input pra permitir re-selecao dos mesmos arquivos
    if (ref.current) ref.current.value = '';
    if (newItems.length !== items.length) {
      onChange(newItems);
    }
  };

  const removeItem = (idx) => {
    const newItems = items.filter((_, i) => i !== idx);
    onChange(newItems);
  };

  const isBusy = uploading || !!compressStatus;

  return (
    <div>
      <input
        ref={ref}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        style={{ display: 'none' }}
        onChange={e => handlePick(e.target.files)}
      />

      {/* Lista de itens ja anexados */}
      {items.length > 0 && (
        <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((it, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              fontSize: 13,
            }}>
              <Paperclip size={13} style={{ color: 'var(--muted)', flexShrink: 0 }} />
              <span style={{
                flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: 'var(--ink)',
              }}>{truncateMiddle(it.filename || 'arquivo.pdf', 60)}</span>
              <a
                href={it.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: 'var(--muted-2)', display: 'inline-flex', padding: 2, flexShrink: 0 }}
                title="Abrir arquivo"
              ><ExternalLink size={14} /></a>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); removeItem(idx); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); removeItem(idx); } }}
                style={{ color: 'var(--muted-2)', padding: 2, cursor: 'pointer', flexShrink: 0 }}
                title="Remover"
              ><X size={14} /></span>
            </div>
          ))}
        </div>
      )}

      {/* Botao adicionar */}
      <button
        type="button"
        disabled={isBusy}
        onClick={() => ref.current?.click()}
        style={{
          width: '100%', height: 40, padding: '0 12px',
          background: items.length > 0 ? 'transparent' : 'var(--surface-2)',
          border: `1px dashed var(--border-2)`,
          borderRadius: 'var(--radius)',
          display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center',
          color: 'var(--muted)',
          fontSize: 13, fontWeight: 500,
          cursor: isBusy ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
          minWidth: 0, overflow: 'hidden',
        }}
      >
        {isBusy ? (
          <>
            <Loader2 size={14} className="spin" style={{ flexShrink: 0 }} />
            <span style={{
              flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              textAlign: 'left',
            }}>{compressStatus || 'Enviando…'}</span>
          </>
        ) : (
          <>
            {items.length > 0 ? <Plus size={14} style={{ flexShrink: 0 }} /> : <Upload size={14} style={{ flexShrink: 0 }} />}
            <span>{items.length > 0 ? 'Adicionar mais um PDF' : 'Clique para anexar PDFs'}</span>
          </>
        )}
      </button>

      <style>{`
        @keyframes fileinput-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .spin { animation: fileinput-spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
